"use client";

import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { stegaClean } from "@sanity/client/stega";
import { CTAButton } from "@/shadcn/ui/button";
import Chip from "@/components/global/ui/Chip";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import type { PageAiFeaturesSchema } from "@/sanity/pages/PageAiFeaturesTemplate";
import { Reveal } from "./ui";
import HeroArtwork from "./HeroArtwork";
import HeroPanels from "./HeroPanels";
import styles from "./ai-features.module.scss";

export default function HeroSection({
  data,
}: {
  data?: PageAiFeaturesSchema["hero"];
}) {
  const root = useRef<HTMLElement>(null);
  const canvas = useRef<HTMLDivElement>(null);
  const copy = useRef<HTMLDivElement>(null);
  const plane = useRef<HTMLDivElement>(null);
  const intro = useRef<HTMLDivElement>(null);
  const [phrase, setPhrase] = useState(0);
  const reducedMotion = useReducedMotion();
  const phrases = stegaClean(
    data?.rotatingPhrases?.length
      ? data.rotatingPhrases
      : data?.subtitle
        ? [data.subtitle]
        : [],
  );
  const phraseKey = phrases.join("\n");

  useEffect(() => {
    const node = root.current;
    const stage = canvas.current;
    const text = copy.current;
    const screen = plane.current;
    if (!node || !stage || !text || !screen) return;
    const clamp = (n: number) => Math.max(0, Math.min(1, n));
    const ease = (n: number) =>
      n < 0.5 ? 4 * n ** 3 : 1 - (-2 * n + 2) ** 3 / 2;
    let frame = 0;
    let previous = -1;
    let mobile = false;
    let start = 0;
    let distance = 1;
    let unit = 1;
    const panels = [
      ...screen.querySelectorAll<HTMLElement>("[data-hero-float]"),
    ];
    const progress = () =>
      reducedMotion ? 0 : clamp((window.scrollY - start) / distance);
    const render = () => {
      frame = 0;
      if (mobile) return;
      const p = progress();
      if (p === previous) return;
      previous = p;
      const zoom = ease(clamp(p / 0.8));
      screen.style.transform = `translateY(${451 * unit * (1 - zoom)}px) scale(${1.4 - 0.4 * zoom})`;
      const opacity = 1 - ease(clamp(p / 0.45));
      text.style.opacity = String(opacity);
      text.style.visibility = opacity === 0 ? "hidden" : "visible";
      text.style.transform = `translateY(${-180 * unit * ease(clamp(p / 0.6))}px)`;
      panels.forEach((panel, i) => {
        const progress = clamp((zoom - 0.55 - i * 0.05) / 0.3);
        const arrived = 1 - (1 - progress) ** 3;
        const direction = panel.dataset.position === "morning" ? 1 : -1;
        panel.style.opacity = String(arrived);
        panel.style.transform = `translate(${direction * 80 * unit * (1 - arrived)}px,${(i % 2 ? 60 : -40) * unit * (1 - arrived)}px)`;
      });
      node.dataset.heroProgress = p.toFixed(3);
    };
    const schedule = () => {
      if (!mobile && !frame && !document.hidden && progress() !== previous)
        frame = requestAnimationFrame(render);
    };
    const resize = () => {
      mobile = window.innerWidth <= 1023;
      if (mobile) {
        [stage, text, screen].forEach((el) => {
          el.style.removeProperty("transform");
          el.style.removeProperty("opacity");
          el.style.removeProperty("visibility");
        });
        node.dataset.heroProgress = "0";
        return;
      }
      const width = stage.offsetWidth;
      unit = width / 1920;
      start = node.getBoundingClientRect().top + window.scrollY;
      distance = Math.max(1, node.offsetHeight - window.innerHeight);
      const fit = Math.min(
        window.innerWidth / width,
        window.innerHeight / (1180 * unit),
      );
      stage.style.transform = `translateX(-50%) scale(${fit})`;
      previous = -1;
      render();
    };
    resize();
    const entrance =
      !reducedMotion && window.innerWidth > 1023
        ? intro.current?.animate(
            [
              { opacity: 0, transform: "translateY(15rem)" },
              { opacity: 1, transform: "translateY(0)" },
            ],
            {
              duration: 1100,
              easing: "cubic-bezier(0.16, 1, 0.3, 1)",
              fill: "backwards",
            },
          )
        : undefined;
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", resize);
    document.addEventListener("visibilitychange", schedule);
    return () => {
      cancelAnimationFrame(frame);
      entrance?.cancel();
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", resize);
      document.removeEventListener("visibilitychange", schedule);
    };
  }, [reducedMotion]);

  useEffect(() => {
    if (reducedMotion || phrases.length < 2) return;
    const timer = window.setInterval(() => {
      const bounds = root.current?.getBoundingClientRect();
      if (
        document.hidden ||
        window.innerWidth <= 1023 ||
        !bounds ||
        bounds.bottom <= 0 ||
        bounds.top >= window.innerHeight ||
        Number(root.current?.dataset.heroProgress) >= 0.45
      )
        return;
      setPhrase((active) => (active + 1) % phrases.length);
    }, 3000);
    return () => window.clearInterval(timer);
  }, [phraseKey, phrases.length, reducedMotion]);

  if (!data) return null;
  return (
    <section ref={root} id="ai-coach" className={styles.hero} data-hero>
      <div className={styles.heroPin}>
        <div
          className={styles.heroBackdrop}
          data-hero-backdrop
          aria-hidden="true"
        >
          <div className={styles.heroHatches} />
        </div>
        <div ref={canvas} className={styles.heroCanvas}>
          <div ref={copy} className={styles.heroCopy}>
            <Reveal className={styles.heroIntro} variant="text">
              <div className={styles.heroHeadline}>
                {data.label && (
                  <Chip text={data.label} className={styles.chip} />
                )}
                <h1 className={styles.heroHeading}>
                  <span className={styles.heroTitle}>{data.title}</span>
                  {phrases.length > 0 && (
                    <>
                      <span className="sr-only">{phrases[0]}</span>
                      <span
                        className={styles.heroRotator}
                        aria-hidden="true"
                        data-hero-rotator
                      >
                        {phrases.map((line, i) => (
                          <span
                            key={line}
                            data-state={
                              i === phrase % phrases.length
                                ? "active"
                                : i ===
                                    (phrase - 1 + phrases.length) %
                                      phrases.length
                                  ? "exit"
                                  : "waiting"
                            }
                          >
                            {line}
                          </span>
                        ))}
                      </span>
                    </>
                  )}
                </h1>
              </div>
              {data.description?.length ? (
                <div className={styles.description}>
                  <PortableText
                    value={data.description}
                    components={richTextComponent}
                  />
                </div>
              ) : null}
            </Reveal>
            <Reveal className={styles.heroCTAs} variant="text">
              {data.cta && <CTAButton data={data.cta} showIncomplete />}
              {data.secondaryCTA && (
                <CTAButton
                  data={data.secondaryCTA}
                  className={styles.secondaryCTA}
                  showIncomplete
                />
              )}
            </Reveal>
          </div>
          <div ref={plane} className={styles.heroPlane}>
            <div ref={intro} className={styles.heroPlaneIntro}>
              <div className={styles.heroVisual}>
                <HeroArtwork
                  image={data.image}
                  mobileImage={data.mobileImage}
                />
              </div>
              <HeroPanels />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
